import {
  APP_IDLE_TIME,
  CUSTOMER_STATUS,
  CUSTOMER_TYPES,
  RESTRICTED_FOLDERS,
  STATUS,
  USER_ACTIVITY_TYPES,
  USER_ROLES,
} from "./constants/common";
import store from "../store/store";
import { resetAuthReducer } from "../store/features/Auth.slice";
import {
  NotificationType,
  resetCommonReducer,
  setToastMessage,
} from "../store/features/Common.slice";
import { resetSignupReducer } from "../store/features/Signup.slice";
import { IUser } from "../interface/Auth";
import { UserActivity } from "../pages/CustomerDetails/CustomerInfo/CustomerInfoStyled";
import { ICustomer } from "../interface/Customer";
import {
  COMMON_MENU,
  PLATFORM_CUSTOMERS,
  PROD_PLATFORM_CUSTOMERS,
  PRODUCT_CUSTOMERS,
  TMenuItem,
} from "./constants/menu";
import { resetRecentlyDelReducer } from "../store/features/RecentlyDeleted.slice";
import { resetCustomerDetailReducer } from "../store/features/CustomerDetails.slice";
import { resetFolderTreeReducer } from "../store/features/FolderTree";
import { resetFileUploadReducer } from "../store/features/UploadDocument.slice";
import { resetProfileReducer } from "../store/features/Dashboard.slice";
import { resetNotificationReducer } from "../store/features/Notification.slice";
import { resetProductDetailsReducer } from "../store/features/ProductDetails.slice";
import { resetProductReducer } from "../store/features/Product.slice";
import { resetUserReducer } from "../store/features/User.slice";
import { resetUserDetailsReducer } from "../store/features/UserDetail.slice";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

export interface ICheckAdminPermission {
  checkCustomerAssignedRole: boolean;
  userId?: string;
}

export const capitalizeFirstLetter = (string: string) => {
  return string?.charAt(0)?.toUpperCase() + string?.slice(1);
};

export const triggerToastMessage = (
  message: string,
  type: NotificationType,
  description = "",
  category = "",
  clickable = false,
) => {
  store.dispatch(
    setToastMessage({
      showToast: true,
      toastMessage: message,
      toastType: type,
      toastDescription: description,
      category,
      clickable,
    }),
  );
};

export const checkUserInactivity = (date1: string) => {
  const lastActive = new Date(date1);
  const now = new Date();
  const diffInSeconds = (now.getTime() - lastActive.getTime()) / 1000;

  return diffInSeconds > APP_IDLE_TIME * 60;
};

export const logoutUser = () => {
  localStorage.clear();
  _resetReducers();
};

export const _resetReducers = () => {
  store.dispatch(resetAuthReducer());
  store.dispatch(resetCommonReducer());
  store.dispatch(resetCustomerDetailReducer());
  store.dispatch(resetFolderTreeReducer());
  store.dispatch(resetFileUploadReducer());
  store.dispatch(resetProfileReducer());
  store.dispatch(resetNotificationReducer());
  store.dispatch(resetProductDetailsReducer());
  store.dispatch(resetProductReducer());
  store.dispatch(resetSignupReducer());
  store.dispatch(resetUserReducer());
  store.dispatch(resetUserDetailsReducer());
  store.dispatch(resetSignupReducer());
  store.dispatch(resetRecentlyDelReducer());
};

export const copyContent = (content: string) => {
  window.navigator.clipboard.writeText(content);
  triggerToastMessage("Copied to clipboard", STATUS.SUCCESS);
};

export const hasNoProducts = () => {
  if (localStorage.getItem("user")) {
    const user: IUser = JSON.parse(localStorage.getItem("user") || "");
    const { is_super_admin, is_node_exists } = user;
    return is_super_admin && !is_node_exists;
  }
  return false;
};

export const isPragmaticUser = () => {
  const userData = localStorage.getItem("user");
  if (userData) {
    const user: IUser = JSON.parse(userData);
    return user?.is_pragmatic_user;
  }
};

export const isSuperAdminUser = () => {
  if (localStorage.getItem("user")) {
    const user: IUser = JSON.parse(localStorage.getItem("user") || "");
    return user?.is_super_admin && user?.is_pragmatic_user;
  }
};

export const isViewerUser = () => {
  if (localStorage.getItem("user")) {
    const user: IUser = JSON.parse(localStorage.getItem("user") || "");
    return !user?.is_super_admin && user?.is_pragmatic_user && !user?.is_admin;
  }
};

export const isIndividualUser = () => {
  if (localStorage.getItem("user")) {
    const user: IUser = JSON.parse(localStorage.getItem("user") || "");
    return !user?.is_pragmatic_user;
  }
};
export const isAdmin = () => {
  if (localStorage.getItem("user")) {
    const user: IUser = JSON.parse(localStorage.getItem("user") || "");
    return user?.is_pragmatic_user && user?.is_admin;
  }
};

export const customerType = () => {
  if (localStorage.getItem("user")) {
    const user: IUser = JSON.parse(localStorage.getItem("user") || "");
    const { customer: { assigned_platforms = 0, assigned_products = 0 } = {} } =
      user;
    if (assigned_products > 0) return CUSTOMER_TYPES.PROD;
    if (assigned_products === 0 && assigned_platforms === 0)
      return CUSTOMER_TYPES.NO_PROD;
    return CUSTOMER_TYPES.PLATFORM;
  }
  return "";
};

export const customerMenu = (): TMenuItem[] => {
  if (localStorage.getItem("user")) {
    const user: IUser = JSON.parse(localStorage.getItem("user") || "");
    const { customer: { assigned_platforms = 0, assigned_products = 0 } = {} } =
      user;
    if (assigned_platforms === 0 && assigned_products > 0) {
      return PRODUCT_CUSTOMERS;
    }
    if (assigned_products === 0 && assigned_platforms > 0) {
      return PLATFORM_CUSTOMERS;
    }
    if (assigned_products > 0 && assigned_platforms > 0) {
      return PROD_PLATFORM_CUSTOMERS;
    }
    return COMMON_MENU;
  }
  return COMMON_MENU;
};

export const isInactiveCustomer = () => {
  if (localStorage.getItem("currentCustomer")) {
    const customer: ICustomer = JSON.parse(
      localStorage.getItem("currentCustomer") || "",
    );
    return customer?.status?.toLowerCase() === CUSTOMER_STATUS.INACTIVE;
  }
};

export type FileType = {
  payload: any;
  fileName: string;
  fileType?: string;
};

export const downloadFile = (fileInfo: FileType) => {
  const { payload, fileName, fileType } = fileInfo;
  const element = document.createElement("a");
  const file = new Blob([payload], {
    type: fileType ?? "text/plain;charset=utf-8",
  });
  element.href = URL.createObjectURL(file);
  element.download = fileName;
  document.body.appendChild(element);
  element.click();
};

export const downloadDoc = (fileUrl: string) => {
  const element = document.createElement("a");
  element.href = fileUrl;
  element.download = fileUrl;
  document.body.appendChild(element);
  element.click();
};

export const byteConverter = (
  bytes: number,
  decimals: number,
  only?: string,
) => {
  const K_UNIT = 1000;
  const SIZES = ["Bytes", "KB", "MB", "GB", "TB", "PB"];

  if (bytes === 0) return "0 Byte";

  if (only === "MB")
    return (bytes / (K_UNIT * K_UNIT)).toFixed(decimals) + " MB";

  let i = Math.floor(Math.log(bytes) / Math.log(K_UNIT));
  i = i < 0 ? 0 : i;
  let resp =
    parseFloat((bytes / Math.pow(K_UNIT, i)).toFixed(decimals)) +
    " " +
    SIZES[i];

  return resp;
};

export const isRestrictedFolder = (nodeName: string) => {
  return RESTRICTED_FOLDERS.includes(nodeName?.toLowerCase());
};

export const checkTime = (time: string) => {
  const now = new Date().toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
  return now === time ? "Now" : time;
};

export const formatTimelineData = (
  timelineData: any[],
  type: string = USER_ACTIVITY_TYPES.customer,
) => {
  const formattedData = timelineData?.map((item: any) => {
    const { message, user, role, event_time, id } = item;
    let data = {
      id: id,
      label: checkTime(event_time),
      children:
        type === USER_ACTIVITY_TYPES.customer ? (
          <UserActivity
            message={message}
            user={user}
            role={role}
            key={event_time}
          />
        ) : (
          message
        ),
    };
    return data;
  });
  return formattedData;
};

export const isCurrentUser = (userId: string) => {
  if (localStorage.getItem("user")) {
    const currUser = JSON.parse(localStorage.getItem("user") || "") || {};
    return userId === currUser?.id;
  }
  return false;
};

export const checkAssignedRole = () => {
  if (localStorage.getItem("currentCustomer")) {
    const currentCus = JSON.parse(
      localStorage.getItem("currentCustomer") || "",
    );
    return !currentCus?.role
      ? USER_ROLES.SUPER_ADMIN
      : currentCus?.role?.toLowerCase();
  }
};

export const _checkActionPermission = (
  role: string,
  name: string,
  isCustomerSpecific: boolean = false,
) => {
  if (role === USER_ROLES.SUPER_ADMIN || role === USER_ROLES.ADMIN) return true;
  return isCustomerSpecific ? !isRestrictedFolder(name) : false;
};

export function extractUtcRangeStrict(
  range: [Dayjs | null, Dayjs | null] | null,
): [string, string] | null {
  if (!range || !range[0] || !range[1]) {
    return null;
  }

  return [range[0].utc().toISOString(), range[1].utc().toISOString()];
}


type DateRange = [Dayjs | null, Dayjs | null] | null;

export const buildDateRangePayloadYYMMDD = (range: DateRange) => {
  if (!range) return {};

  const [start, end] = range;

  return {
    ...(start && {
      Start_Date: start.startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    }),
    ...(end && {
      End_Date: end.startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    }),
  };
};